import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { useToastStore } from '../../../stores/toastStore';
import { useDocumentTypes } from '../../schema/hooks/useDocumentTypes';
import { reprocessDocument } from '../../dashboard/api';

interface ClassificationCorrectionControlProps {
  jobId: string;
  /**
   * Layout + emphasis:
   *   - `inline`: a collapsible «¿Tipo incorrecto?» affordance for a document that already has a type
   *     (correcting a mis-classification).
   *   - `panel`: an always-open primary picker for a job whose classification was left unresolved
   *     (assigning the type is the main action, so no collapse and a primary button).
   */
  mode: 'inline' | 'panel';
  /** The job's current type slug; excluded from the picker in `inline` mode. Null/absent for `panel`. */
  currentSlug?: string | null;
}

/**
 * Type-picker + confirm that reprocesses the document under a different published type via
 * <c>POST /documents/{id}/reprocess</c> with <c>newDocumentTypeSlug</c>. On success it navigates to the
 * freshly-created job (the original is left intact). Shared by the reviewer's «¿Tipo incorrecto?»
 * correction affordance and the «No se pudo determinar el tipo» unresolved panel.
 */
export function ClassificationCorrectionControl({
  jobId,
  mode,
  currentSlug,
}: ClassificationCorrectionControlProps): JSX.Element | null {
  const pushToast = useToastStore((s) => s.push);
  const navigate = useNavigate();
  const { data: docTypes, isLoading } = useDocumentTypes();

  const [open, setOpen] = useState(mode === 'panel');
  const [selected, setSelected] = useState('');

  const published = (docTypes ?? []).filter(
    (t) => t.isPublished && t.slug !== (currentSlug ?? undefined),
  );

  const mutation = useMutation({
    mutationFn: (slug: string) => reprocessDocument(jobId, { newDocumentTypeSlug: slug }),
    onSuccess: (res) => {
      pushToast({
        tone: 'success',
        title: 'Documento reprocesado',
        description: `Nuevo job ${res.jobId} con el tipo elegido.`,
      });
      navigate(`/documents/${encodeURIComponent(res.jobId)}`);
    },
    onError: (err: unknown) => {
      pushToast({
        tone: 'error',
        title: 'No se pudo reprocesar',
        description: err instanceof Error ? err.message : 'Error desconocido.',
      });
    },
  });

  if (mode === 'inline' && !open) {
    return (
      <button
        type="button"
        className="text-xs font-medium text-primary hover:underline"
        onClick={() => setOpen(true)}
        data-testid="classification-correct-open"
      >
        ¿Tipo incorrecto?
      </button>
    );
  }

  const canSubmit = selected !== '' && !mutation.isPending;

  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="classification-correction">
      <div className="min-w-[14rem] flex-1">
        <label className="label" htmlFor={`correct-type-${jobId}`}>
          {mode === 'panel' ? 'Tipo de documento' : 'Reprocesar con otro tipo'}
        </label>
        <select
          id={`correct-type-${jobId}`}
          className="input"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          disabled={isLoading || mutation.isPending}
          data-testid="classification-correct-select"
        >
          <option value="">
            {published.length === 0 ? 'No hay tipos publicados' : 'Selecciona un tipo…'}
          </option>
          {published.map((t) => (
            <option key={t.slug} value={t.slug}>
              {t.name} ({t.slug})
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        className={mode === 'panel' ? 'btn-primary' : 'btn-secondary'}
        onClick={() => canSubmit && mutation.mutate(selected)}
        disabled={!canSubmit}
        data-testid="classification-correct-submit"
      >
        {mutation.isPending
          ? 'Reprocesando…'
          : mode === 'panel'
            ? 'Asignar tipo y procesar'
            : 'Reprocesar'}
      </button>
      {mode === 'inline' ? (
        <button
          type="button"
          className="text-xs text-slate-500 hover:underline"
          onClick={() => setOpen(false)}
          disabled={mutation.isPending}
        >
          Cancelar
        </button>
      ) : null}
    </div>
  );
}
